import { Button, CardActions } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { ComponentProps, forwardRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroller'

import { Breadcrumb, FaqSettingMinimal, Hierarchy, TagPageContext } from '@karakuri-web/faq-type'

import { QAContent, QAHeader, QAPanel } from '../components'
import { useFaqRouting } from '../contexts/FaqRoutingContext'
import { useCategoryFaq } from '../hooks/useFaqDataQuery'

function generateGAQuery(pageContext: TagPageContext) {
  const categoryId = pageContext.parentId || pageContext.id
  const categoryName = pageContext.parentTitle || pageContext.title
  const subCategoryId = pageContext.parentId ? pageContext.id : null
  const subCategoryName = pageContext.parentTitle ? pageContext.title : null

  return `?category_id=${encodeURIComponent(categoryId)}&category_name=${encodeURIComponent(
    categoryName
  )}${subCategoryId ? `&sub_category_id=${encodeURIComponent(subCategoryId)}` : ''}${
    subCategoryName ? `&sub_category_name=${encodeURIComponent(subCategoryName)}` : ''
  }`
}

interface Props<T extends FaqSettingMinimal = FaqSettingMinimal> {
  breadcrumb: Breadcrumb
  data: { settingYaml: T }
  pageContext: TagPageContext
  hierarchies: Hierarchy[]
}

/**
 * タグ/カテゴリページウィジェット
 * カテゴリに紐づくFAQを表示する
 */
export function TagWidget<T extends FaqSettingMinimal = FaqSettingMinimal>({
  data,
  pageContext,
  hierarchies,
}: Props<T>) {
  const { Link } = useFaqRouting()
  const botId = data.settingYaml.botId || ''
  const tenantId = data.settingYaml.tenantId
  const [page, setPage] = useState(1)

  const { loading, data: faqByCategory } = useCategoryFaq({
    botId,
    categories: [pageContext.id, ...(pageContext.children || [])].filter(x => x),
    tenantId,
    skip: !botId,
  })

  const handleMoreClick = () => setPage(page + 1)
  const totalCount = faqByCategory?.filterByCategory?.length || 0
  const perPage = 5

  const maxLoadingCount = page * perPage
  const hasNext = maxLoadingCount < totalCount

  if (
    loading ||
    !faqByCategory ||
    !faqByCategory.filterByCategory ||
    faqByCategory.filterByCategory.length === 0
  ) {
    return null
  }
  return (
    <QAPanel>
      <QAHeader
        title={`${totalCount}件中 1〜${
          maxLoadingCount < totalCount ? maxLoadingCount : totalCount
        }件`}
        titleClass="faq-pagination"
      />
      <InfiniteScroll
        pageStart={0}
        hasMore={hasNext}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        loader={null as any}
        loadMore={handleMoreClick}
      >
        {faqByCategory.filterByCategory.map(({ title, id }, index) => {
          if (maxLoadingCount - 1 < index) {
            return null
          }
          const h = [
            ...hierarchies,
            { title: pageContext.title, link: `/category/${pageContext.id}/` },
          ]
          const LinkComponent = forwardRef<HTMLAnchorElement, ComponentProps<typeof Link>>(
            function LinkComponent(props, ref) {
              const query = generateGAQuery(pageContext)
              return (
                <Link
                  ref={ref}
                  {...props}
                  to={`/answer/${id}/${query}`}
                  state={{ hierarchies: h }}
                />
              )
            }
          )
          return <QAContent key={id} question={title || ''} LinkComponent={LinkComponent} />
        })}
      </InfiniteScroll>

      {hasNext && (
        <CardActions>
          <Button
            className="faq-button faq-button-more"
            variant="contained"
            color="secondary"
            fullWidth
            onClick={handleMoreClick}
          >
            もっと見る
            <ExpandMoreIcon className="faq-button-icon" />
          </Button>
        </CardActions>
      )}
    </QAPanel>
  )
}
